import {
  destroyAdmin,
  getAdmin,
  getAdminBlob,
  postAdmin,
  postAdminForm,
  putAdmin,
  putAdminForm,
} from "../axios/api/AdminBaseUrl";
import { buildQueryString } from "../utils/queryParams";

export const property = {
  // ─── Property CRUD ────────────────────────────────────────────────────────

  list: {
    key: "list",
    call: (page, search, statusFilter, type, ordering) => {
      const query = buildQueryString({
        page,
        search,
        status: statusFilter,
        type,
        ordering,
      });
      return getAdmin(`/admin/property/?${query}`);
    },
  },

  details: {
    key: "details",
    call: (id) => {
      return getAdmin(`/admin/property/${id}/`);
    },
  },

  delete: {
    key: "delete",
    call: (id, permanent = false) => {
      const query = permanent ? "?permanent=true" : "";
      return destroyAdmin(`/admin/property/${id}/${query}`);
    },
  },

  add: {
    key: "add",
    call: (payload) => {
      return postAdminForm(`/admin/property/`, payload);
    },
  },

  update: {
    key: "update",
    call: (Id, payload) => {
      return putAdminForm(`/admin/property/${Id}/`, payload);
    },
  },

  // ─── Dropdowns ────────────────────────────────────────────────────────────

  /** Property managers dropdown (is_property_manager=True) */
  role: {
    key: "role",
    call: (page, search) => {
      const query = buildQueryString({ page, search, page_size: 100 });
      return getAdmin(`/admin/property_managers_dropdown/?${query}`);
    },
  },

  /** Property type dropdown */
  type: {
    key: "type",
    call: () => {
      return getAdmin(`/property_type_dropdown/`);
    },
  },

  /** All properties for dropdowns */
  allproperty: {
    key: "allproperty",
    call: (page, search) => {
      const query = buildQueryString({ page, search, page_size: 100 });
      return getAdmin(`/admin/property/?${query}`);
    },
  },

  /** Company dropdown */
  company: {
    key: "company",
    call: (page, search) => {
      const query = buildQueryString({ page, search, page_size: 100 });
      return getAdmin(`/admin/company_dropdown/?${query}`);
    },
  },

  /** Properties filtered by company IDs — "all" + exclude_ids, or explicit comma list */
  companyList: {
    key: "companyList",
    call: ({ companyIdsParam, excludeIdsParam, page, search }) => {
      const query = buildQueryString({
        company_ids: companyIdsParam,
        exclude_ids: excludeIdsParam,
        page,
        search,
      });
      return getAdmin(`/admin/property_dropdown_by_companies/?${query}`);
    },
  },

  /** Property managers scoped to a specific company */
  rolewithcompanyid: {
    key: "rolewithcompanyid",
    call: (page, search, companyId) => {
      const query = buildQueryString({
        company_id: companyId,
        page,
        search,
        page_size: 100,
      });
      return getAdmin(`/admin/property_managers_dropdown/?${query}`);
    },
  },

  /** Properties filtered by a single company (list page) */
  companybasedpropertylist: {
    key: "companybasedpropertylist",
    call: (companyId, page, search, statusFilter, type, managers) => {
      const query = buildQueryString({
        client_company_id: companyId,
        page,
        search,
        status: statusFilter,
        property_type: type,
        property_manager: managers,
      });
      return getAdmin(`/admin/property/?${query}`);
    },
  },

  // ─── Bulk Upload ──────────────────────────────────────────────────────────

  excel: {
    key: "excel",
    call: (companyId, payload) => {
      return postAdminForm(
        `/admin/property-excel-upload/?company=${companyId}`,
        payload,
      );
    },
  },

  // ─── Bulk Upload ──────────────────────────────────────────────────────────

  buildingExcel: {
    key: "buildingExcel",
    call: (propertyId, payload) => {
      return postAdminForm(
        `/admin/building-excel-upload/${propertyId}/`,
        payload,
      );
    },
  },

  // ─── Master Import (company-level: properties + buildings + units) ────────

  masterExcel: {
    key: "masterExcel",
    call: (companyId, payload) => {
      return postAdminForm(
        `/admin/master-excel-upload/?company=${companyId}`,
        payload,
      );
    },
  },

  excelUrls: {
    key: "excelUrls",
    call: () => {
      return getAdmin(`/admin/get_excel_urls/`);
    },
  },

  // ─── Building CRUD ────────────────────────────────────────────────────────

  buildinglist: {
    key: "buildinglist",
    call: (id, page, search, statusFilter, ordering) => {
      const query = buildQueryString({
        property_id: id,
        page,
        search,
        status: statusFilter,
        ordering,
      });
      return getAdmin(`/admin/building/?${query}`);
    },
  },

  buildingadd: {
    key: "buildingadd",
    call: (id, payload) => {
      return postAdminForm(`/admin/building/?property_id=${id}`, payload);
    },
  },

  buildingdetails: {
    key: "buildingdetails",
    call: (id) => {
      return getAdmin(`/admin/building/${id}/`);
    },
  },

  buildingupdate: {
    key: "buildingupdate",
    call: (Id, payload) => {
      return putAdminForm(`/admin/building/${Id}/`, payload);
    },
  },

  buildingdelete: {
    key: "buildingdelete",
    call: (block_id, permanent = false) => {
      const query = permanent ? "?permanent=true" : "";
      return destroyAdmin(`/admin/building/${block_id}/${query}`);
    },
  },

  // ─── Unit CRUD ────────────────────────────────────────────────────────────

  unitadd: {
    key: "unitadd",
    call: (id, payload) => {
      return postAdmin(`/admin/unit/?building_id=${id}`, payload);
    },
  },

  getlist: {
    key: "getlist",
    call: (id) => {
      return getAdmin(`/admin/unit/${id}`);
    },
  },

  unitdelete: {
    key: "unitdelete",
    call: (id, permanent = false) => {
      const query = permanent ? "?permanent=true" : "";
      return destroyAdmin(`/admin/unit/${id}/${query}`);
    },
  },

  unitupdate: {
    key: "unitupdate",
    call: (id, payload) => {
      return putAdmin(`/admin/unit/${id}/`, payload);
    },
  },

  // ─── Reports ──────────────────────────────────────────────────────────────

  /**
   * Property Report Download
   *
   * Backend: GET /admin/property_report/
   * Accepted query params (all optional):
   *   - company   : UUID   → filter by client management company
   *   - property  : UUID   → filter by specific property  (backend fix required — see note)
   *   - status    : 0 | 1  → inactive / active
   *   - search    : string → searches property_name, company_name
   *
   * NOTE: Add property filter to backend PropertyReportAPIView:
   *   property_param = request.query_params.get("property", "").strip()
   *   if property_param:
   *       valid_uuids, _ = validate_and_filter_uuids(property_param)
   *       filter_q &= Q(id__in=valid_uuids)
   */
  reportDownload: {
    key: "reportDownload",
    /**
     * @param {object} filters
     * @param {string} [filters.company]   - company UUID
     * @param {string} [filters.property]  - property UUID
     * @param {string} [filters.status]    - "0" | "1" | ""
     * @param {string} [filters.search]    - free text
     */
    call: ({ company = "", property = "", status = "", search = "" } = {}) => {
      const params = new URLSearchParams();
      if (company) params.append("company", company);
      if (property) params.append("property", property);
      if (status) params.append("status", status);
      if (search) params.append("search", search);

      const query = params.toString();
      return getAdminBlob(`/admin/property_report/${query ? `?${query}` : ""}`);
    },
  },

  /**
   * Building Report Download
   *
   * Backend: GET /admin/building_report/
   * Accepted query params (all optional):
   *   - company  : UUID
   *   - property : UUID
   *   - building : UUID
   *   - status   : 0 | 1
   *   - search   : string
   */
  buildingReportDownload: {
  key: "buildingReportDownload",
  call: ({
    company = "",
    property = "",
    building = "",
    status = "",
    search = "",
  } = {}) => {
    const params = new URLSearchParams();

    if (company) params.append("company", company);
    if (property) params.append("property", property);
    if (building) params.append("building", building);
    if (status) params.append("status", status);
    if (search) params.append("search", search);

    const query = params.toString();

    return getAdminBlob(
      `/admin/building_report/${query ? `?${query}` : ""}`
    );
  },
},

/**
 * Unit Report Download
 *
 * Backend: GET /admin/unit_report/
 * Accepted query params (all optional):
 *   - company  : UUID
 *   - property : UUID
 *   - building : UUID
 *   - unit     : UUID
 *   - status   : 0 | 1
 *   - search   : string
 */
unitReportDownload: {
  key: "unitReportDownload",
  call: ({
    company = "",
    property = "",
    building = "",
    unit = "",
    status = "",
    search = "",
  } = {}) => {
    const params = new URLSearchParams();

    if (company) params.append("company", company);
    if (property) params.append("property", property);
    if (building) params.append("building", building);
    if (unit) params.append("unit", unit);
    if (status) params.append("status", status);
    if (search) params.append("search", search);

    const query = params.toString();

    return getAdminBlob(
      `/admin/unit_report/${query ? `?${query}` : ""}`
    );
  },
},
};